import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  Claim,
  ClaimPostingDetails,
  ClaimPostingRecord,
  TrackingRecord,
} from '@dataTypes/claimFeature.types'
import {
  fetchAllFeatureClaims,
  addFeatureClaim,
  updateFeatureClaim,
  removeFeatureClaim,
  fetchAllTrackingRecords,
  fetchClaimPostings,
  fetchClaimPostingDetails,
  updateClaimStatus,
  addTrackingRecord,
  addOrUpdateNoteThunk,
  addOrUpdateAttachmentThunk,
} from '@store/actions/claimFeature.action'
import { addNote, updateNote } from '@store/actions/note.action'
import { INote, NoteContextType } from '@dataTypes/note.type'
interface ClaimState {
  claims: Claim[]
  trackingRecords?: TrackingRecord[]
  claimPostings?: ClaimPostingRecord[]
  claimPostingDetails?: ClaimPostingDetails
  loading: boolean
  trackingLoading?: boolean
  claimPostingsLoading?: boolean
  claimPostingDetailsLoading?: boolean
  error: string | null
}

const initialState: ClaimState = {
  claims: [],
  trackingRecords: [],
  claimPostings: [],
  claimPostingDetails: undefined,
  loading: false,
  trackingLoading: false,
  claimPostingsLoading: false,
  claimPostingDetailsLoading: false,
  error: null,
}

const claimFeatureSlice = createSlice({
  name: 'claimFeature',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const syncClaimNotesFromNoteMutation = (
      state: ClaimState,
      note: INote,
    ): void => {
      if (note.context_type !== NoteContextType.CLAIMS || !note.context_id) {
        return
      }

      const contextId = String(note.context_id)
      const claimIndex = state.claims.findIndex(
        (claim) =>
          String(claim.id) === contextId ||
          String(claim.claim_id) === contextId,
      )
      if (claimIndex === -1) {
        return
      }

      const existingNotes = state.claims[claimIndex].notes ?? []
      const noteIndex = existingNotes.findIndex(
        (n) => n.note_id === note.note_id,
      )
      const nextNotes =
        noteIndex === -1
          ? [...existingNotes, note]
          : existingNotes.map((n, idx) => (idx === noteIndex ? note : n))

      state.claims[claimIndex] = {
        ...state.claims[claimIndex],
        notes: nextNotes,
      }
    }

    builder
      .addCase(fetchAllFeatureClaims.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAllFeatureClaims.fulfilled,
        (state, action: PayloadAction<Claim[]>) => {
          state.loading = false
          state.claims = action.payload
        },
      )
      .addCase(fetchAllFeatureClaims.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      .addCase(fetchAllTrackingRecords.pending, (state) => {
        state.trackingLoading = true
      })
      .addCase(
        fetchAllTrackingRecords.fulfilled,
        (state, action: PayloadAction<TrackingRecord[]>) => {
          state.trackingLoading = false
          state.trackingRecords = action.payload
        },
      )
      .addCase(fetchAllTrackingRecords.rejected, (state) => {
        state.trackingLoading = false
      })

      .addCase(fetchClaimPostings.pending, (state) => {
        state.claimPostingsLoading = true
      })
      .addCase(
        fetchClaimPostings.fulfilled,
        (state, action: PayloadAction<ClaimPostingRecord[]>) => {
          state.claimPostingsLoading = false
          state.claimPostings = action.payload
        },
      )
      .addCase(fetchClaimPostings.rejected, (state) => {
        state.claimPostingsLoading = false
      })

      .addCase(fetchClaimPostingDetails.pending, (state) => {
        state.claimPostingDetailsLoading = true
        state.error = null
      })
      .addCase(
        fetchClaimPostingDetails.fulfilled,
        (state, action: PayloadAction<ClaimPostingDetails>) => {
          state.claimPostingDetailsLoading = false
          state.claimPostingDetails = action.payload
        },
      )
      .addCase(fetchClaimPostingDetails.rejected, (state, action) => {
        state.claimPostingDetailsLoading = false
        state.error = action.payload as string
      })

      .addCase(
        addFeatureClaim.fulfilled,
        (state, action: PayloadAction<Claim>) => {
          state.claims.push(action.payload)
        },
      )

      .addCase(
        updateFeatureClaim.fulfilled,
        (state, action: PayloadAction<Claim>) => {
          const index = state.claims.findIndex(
            (claim) => claim.id === action.payload.id,
          )

          if (index !== -1) {
            state.claims[index] = {
              ...state.claims[index],
              ...action.payload,
              procedures:
                action.payload.procedures &&
                action.payload.procedures.length > 0
                  ? action.payload.procedures
                  : state.claims[index].procedures,
              updatedAt: new Date().toISOString().split('T')[0],
            }
          }
        },
      )

      .addCase(
        removeFeatureClaim.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.claims = state.claims.filter(
            (claim) => claim.id !== action.payload,
          )
        },
      )

      .addCase(
        updateClaimStatus.fulfilled,
        (state, action: PayloadAction<Claim>) => {
          const updatedClaim = action.payload
          const index = state.claims.findIndex(
            (claim) => claim.id === updatedClaim.id,
          )
          if (index !== -1) {
            state.claims[index] = updatedClaim
          }
        },
      )

      .addCase(
        addTrackingRecord.fulfilled,
        (state, action: PayloadAction<TrackingRecord>) => {
          if (!state.trackingRecords) {
            state.trackingRecords = []
          }

          state.trackingRecords.push(action.payload)
        },
      )

      .addCase(addTrackingRecord.rejected, (state, action) => {
        state.error = action.payload as string
      })

      .addCase(
        addOrUpdateNoteThunk.fulfilled,
        (state, action: PayloadAction<Claim>) => {
          const updatedClaim = action.payload
          const index = state.claims.findIndex(
            (claim) => claim.id === updatedClaim.id,
          )
          if (index !== -1) {
            state.claims[index] = updatedClaim
          }
        },
      )

      .addCase(
        addOrUpdateAttachmentThunk.fulfilled,
        (state, action: PayloadAction<Claim>) => {
          const updatedClaim = action.payload
          const index = state.claims.findIndex(
            (claim) => claim.id === updatedClaim.id,
          )
          if (index !== -1) {
            state.claims[index] = updatedClaim
          }
        },
      )
      .addCase(addNote.fulfilled, (state, action: PayloadAction<INote>) => {
        syncClaimNotesFromNoteMutation(state, action.payload)
      })
      .addCase(updateNote.fulfilled, (state, action: PayloadAction<INote>) => {
        syncClaimNotesFromNoteMutation(state, action.payload)
      })
  },
})

export default claimFeatureSlice.reducer
